/** Unit placeholders: a capsule tinted by role/faction, a facing nub, a ground disc and a pick box. */
import * as THREE from 'three';
import type { UnitId } from '@keystone/sim';
import { TIER_SCALE, unitColor } from '../palette';
import type { RenderUnit } from '../types';
import { layFlat, toThree } from './coords';

const DEAD_COLOR = new THREE.Color(0x555555);

export class UnitView {
  readonly group = new THREE.Group();
  readonly hitbox: THREE.Mesh;
  readonly height: number;
  private readonly body: THREE.Mesh;
  private readonly bodyMat: THREE.MeshLambertMaterial;
  private readonly nub: THREE.Mesh;
  private readonly baseColor: THREE.Color;
  private flashUntil = 0;
  private flashColor = new THREE.Color(0xffffff);
  private wasAlive = true;

  constructor(readonly id: UnitId, u: RenderUnit) {
    const scale = u.kind === 'npc' && u.tier ? TIER_SCALE[u.tier] : 1;
    this.height = 2 * scale;
    const r = Math.min(Math.max(0.42, u.radius * 0.85), this.height * 0.38) * (u.kind === 'npc' ? Math.max(1, scale * 0.8) : 1);
    const bodyR = Math.min(r, this.height * 0.45);
    this.baseColor = new THREE.Color(unitColor(u));
    this.bodyMat = new THREE.MeshLambertMaterial({ color: this.baseColor.clone() });
    if (u.isPlayer) this.bodyMat.emissive.set(0x1a2a40);
    this.body = new THREE.Mesh(new THREE.CapsuleGeometry(bodyR, Math.max(0.05, this.height - 2 * bodyR), 4, 12), this.bodyMat);
    this.body.position.y = this.height / 2;
    this.group.add(this.body);

    this.nub = new THREE.Mesh(new THREE.ConeGeometry(bodyR * 0.45, bodyR * 1.1, 10), new THREE.MeshLambertMaterial({ color: 0xf2f2f2 }));
    this.nub.rotation.z = -Math.PI / 2; // cone tip → +x (front)
    this.nub.position.set(bodyR + bodyR * 0.45, this.height * 0.68, 0);
    this.group.add(this.nub);

    // Collision footprint, also a cheap blob shadow.
    const disc = new THREE.Mesh(new THREE.CircleGeometry(Math.max(0.4, u.radius), 24), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.35, depthWrite: false }));
    layFlat(disc);
    disc.position.y = 0.03;
    this.group.add(disc);

    const pickR = Math.max(0.9, bodyR * 1.3);
    this.hitbox = new THREE.Mesh(new THREE.CylinderGeometry(pickR, pickR, this.height + 0.6, 8), new THREE.MeshBasicMaterial({ visible: false }));
    this.hitbox.position.y = this.height / 2;
    this.hitbox.userData['unitId'] = id;
    this.group.add(this.hitbox);
  }

  flash(color: THREE.ColorRepresentation, untilMs: number): void {
    this.flashColor.set(color);
    this.flashUntil = untilMs;
  }

  update(u: RenderUnit, timeMs: number): void {
    toThree(u.pos, 0, this.group.position);
    this.group.rotation.y = u.facing;
    if (u.alive !== this.wasAlive) {
      this.wasAlive = u.alive;
      this.body.rotation.z = u.alive ? 0 : Math.PI / 2;
      this.body.position.y = u.alive ? this.height / 2 : 0.4;
      this.nub.visible = u.alive;
    }
    const c = this.bodyMat.color;
    if (!u.alive) c.copy(DEAD_COLOR);
    else if (timeMs < this.flashUntil) c.copy(this.baseColor).lerp(this.flashColor, 0.65);
    else c.copy(this.baseColor);
  }

  dispose(): void {
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
    });
  }
}

/** Ground ring under the selected / hovered unit. */
export class SelectionRing {
  readonly mesh: THREE.Mesh;
  private readonly mat: THREE.MeshBasicMaterial;

  constructor(opacity: number) {
    this.mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide });
    this.mesh = new THREE.Mesh(new THREE.RingGeometry(0.85, 1, 48), this.mat);
    layFlat(this.mesh);
    this.mesh.visible = false;
  }

  place(u: RenderUnit | undefined, hostile: boolean): void {
    if (!u) {
      this.mesh.visible = false;
      return;
    }
    this.mesh.visible = true;
    const r = Math.max(0.9, u.radius * 1.15) + 0.4;
    this.mesh.scale.set(r, r, 1);
    toThree(u.pos, 0.04, this.mesh.position);
    this.mat.color.set(hostile ? 0xff4a3a : 0x5ef08a);
  }
}
